import React, { useCallback, useMemo, useRef, useState } from "react";
import { Button } from "antd";
import { connect } from "dva";
import { getAvailableTransporterShipment, getTransporterAvailableLoads } from "@/services/shipments_new";
import AllShipmentsMap from "@/components/AllShipmentsMap";
import CantDispatchAlert from "@/components/CantDispatchAlert";
import DispatchStepsModal from "@/components/DispatchStepsModal";
import ListView from "@/components/ListView";
import { ListToBeSorted } from "@/components/SortAndFilter";
import useAvailableShipmentsTableColumns from "./utils/useAvailableShipmentsTableColumns";

const TransporterAvailableShipmentsListView = ({ configPerm }) => {
  const actionRef = useRef();

  const columns = useAvailableShipmentsTableColumns();

  const [totalItems, setTotalItems] = useState(0);
  const [selectedShipmentsIds, setSelectedShipmentsIds] = useState([]);
  const [selectedShipments, setSelectedShipments] = useState([]);
  const canAcceptShipments = useMemo(() => !!configPerm["CoreService.AvailableLoads.Accept"], [configPerm]);

  /* State passed as props to DispatchStepsModal component */
  const [dispatchStepsModalVisible, setDispatchStepsModalVisible] = useState(false);

  const rowSelection = useMemo(() => {
    if (!configPerm["CoreService.AvailableLoads.Accept"]) {
      return false;
    }

    return {
      selectedRowKeys: selectedShipmentsIds,
      onChange: (_, selectedRows) => setSelectedShipmentsIds(selectedRows.map(row => row.id)),
      onSelectNone: () => {
        setSelectedShipmentsIds([]);
        setSelectedShipments([]);
      },
    };
  }, [configPerm, selectedShipmentsIds, setSelectedShipmentsIds, setSelectedShipments]);

  const getShipmentsData = useCallback(async () => {
    const shipmentsData = await Promise.all(selectedShipmentsIds?.map(async id => getAvailableTransporterShipment(id)));

    return shipmentsData ?? [];
  }, [selectedShipmentsIds]);

  /**
   * Pull up the DispatchStepsModal with the currently selected list of shipments for dispatch
   * @param {array} selectedShipmentsData List of shipment items selected from the table that will be dispatched by the user.
   */
  const openDispatchStepsModal = useCallback(async () => {
    const selectedShipmentsData = await getShipmentsData();
    setSelectedShipments(selectedShipmentsData);
    setDispatchStepsModalVisible(true);
  }, [getShipmentsData]);

  const fetchShipmentsList = useCallback(async ({ filters }) => {
    try {
      const response = await getTransporterAvailableLoads(filters);
      const { totalCount = 0, items = [] } = response?.data ?? {};
      setTotalItems(totalCount);

      return {
        data: items,
        total: totalCount,
        success: true,
      };
    } catch (error) {
      console.error(error);
      return {
        data: [],
        total: 0,
      };
    }
  }, []);

  const toolBarRender = useMemo(
    () =>
      function render() {
        const buttonList = [];

        if (canAcceptShipments && selectedShipmentsIds.length > 0) {
          buttonList.push(
            <Button type="primary" onClick={openDispatchStepsModal}>
              Accept {selectedShipmentsIds.length} Shipment
              {selectedShipmentsIds.length > 1 ? "s" : ""}
            </Button>,
          );
        }

        return buttonList;
      },
    [canAcceptShipments, selectedShipmentsIds, openDispatchStepsModal],
  );

  return (
    <ListView
      ref={actionRef}
      filterType={ListToBeSorted.TransporterShipments}
      columns={columns}
      baseDetailsUrl="/loads/shipments/view"
      detailsUrlParamName="shipmentId"
      totalItems={totalItems}
      request={fetchShipmentsList}
      toolBarRender={toolBarRender}
      rowSelection={rowSelection}
      header={
        <>
          {!canAcceptShipments && <CantDispatchAlert style={{ marginLeft: 20, marginBottom: 16 }} />}
          <AllShipmentsMap />
        </>
      }
    >
      <DispatchStepsModal
        visible={dispatchStepsModalVisible}
        setVisible={setDispatchStepsModalVisible}
        selectedShipments={selectedShipments}
        refreshParentTable={() => {
          setSelectedShipments([]);
          setSelectedShipmentsIds([]);
          actionRef?.current?.reload();
        }}
      />
    </ListView>
  );
};

export default connect(({ configs }) => ({
  configPerm: configs.configPerm,
}))(TransporterAvailableShipmentsListView);
